/**
 * Aba "Preço × concorrência" — pedido da Mariana em 21/09/2026: sair do painel de score e virar
 * uma aba própria. O dado é o `price_to_win`/comparação de preço que o ML devolve por anúncio
 * (`pc` na coleta): status, preço atual, preço de referência e diferença em %. Só entram anúncios
 * em que o ML fez a comparação — os outros não têm concorrente identificado, e a aba diz isso.
 * Campos da coleta que ainda não têm significado confirmado NÃO são exibidos.
 */
import { useMemo, useState } from 'react'
import { Box, Button, Card, CardContent, Chip, Grid, Link, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, alpha, useTheme } from '@mui/material'
import { ChartEmpty } from '../../components/ChartEmpty'
import { KpiCard } from '../../components/KpiCard'
import { fmtBrl } from '../../api/bridge'
import { NOME_CONTA, sum, type Row } from './logic'

const nf = (n: number | null | undefined, d = 0): string =>
  n === null || n === undefined || Number.isNaN(n) ? '—' : n.toLocaleString('pt-BR', { minimumFractionDigits: d, maximumFractionDigits: d })
const pctFmt = (n: number | null | undefined, d = 1): string => (n === null || n === undefined || Number.isNaN(n) ? '—' : `${nf(n, d)}%`)

/** Status que o ML devolve na comparação, com a leitura operacional de cada um. */
export const PST: Record<string, { cor: 'error' | 'warning' | 'success'; label: string; explica: string }> = {
  with_benchmark_highest: { cor: 'error', label: 'Acima do mercado', explica: 'O ML achou o mesmo produto mais barato em outro vendedor e o nosso está acima da referência. Perde posição na busca e elegibilidade a campanha.' },
  with_benchmark_high: { cor: 'warning', label: 'Um pouco acima', explica: 'Acima da referência, por pouco. Um ajuste pequeno recoloca o anúncio na faixa.' },
  no_benchmark_ok: { cor: 'success', label: 'Dentro da faixa', explica: 'O ML não aponta concorrente mais barato; o preço está na faixa esperada.' },
  no_benchmark_lowest: { cor: 'success', label: 'Menor preço', explica: 'Somos o menor preço entre os comparados pelo ML.' },
}
const ORDEM = ['with_benchmark_highest', 'with_benchmark_high', 'no_benchmark_ok', 'no_benchmark_lowest']
type SortKey = 'r' | 'pdif' | 'pgap' | 'vi' | 'pcur'
const PAGE = 60

export function AbaConcorrencia({ rowsAll, diasJanela }: { rowsAll: Row[]; diasJanela: number }) {
  const t = useTheme()
  const [pst, setPst] = useState('')
  const [conta, setConta] = useState('')
  const [busca, setBusca] = useState('')
  const [sort, setSort] = useState<{ k: SortKey; dir: 1 | -1 }>({ k: 'r', dir: -1 })
  const [visivel, setVisivel] = useState(PAGE)

  const comparados = useMemo(() => rowsAll.filter((r) => r.pst), [rowsAll])
  const semComparacao = rowsAll.length - comparados.length
  const porStatus = useMemo(() => ORDEM.map((k) => ({ k, ...PST[k], rows: comparados.filter((r) => r.pst === k) })).concat(
    [...new Set(comparados.map((r) => r.pst!))].filter((k) => !PST[k]).map((k) => ({ k, cor: 'warning' as const, label: k, explica: 'status não documentado — exibido como veio da API', rows: comparados.filter((r) => r.pst === k) })),
  ), [comparados])
  const caros = comparados.filter((r) => r.caro)
  const receitaCaros = sum(caros, (r) => r.r)
  const difMedia = caros.length ? sum(caros, (r) => r.pdif) / caros.filter((r) => r.pdif != null).length : null
  const gapTotal = sum(caros, (r) => (r.pgap != null && r.pgap > 0 ? r.pgap * r.u : 0))

  const lista = useMemo(() => {
    const term = busca.trim().toLowerCase()
    const base = comparados.filter((r) => (!pst || r.pst === pst) && (!conta || r.acc === conta) && (!term || r.t.toLowerCase().includes(term) || r.id.toLowerCase().includes(term)))
    const v = (r: Row): number => {
      switch (sort.k) {
        case 'pdif': return r.pdif ?? -999
        case 'pgap': return r.pgap ?? -999
        case 'vi': return r.vi
        case 'pcur': return r.pcur ?? -1
        default: return r.r || 0
      }
    }
    return [...base].sort((a, b) => (v(a) - v(b)) * sort.dir)
  }, [comparados, pst, conta, busca, sort])

  const th = (k: SortKey, label: string) => (
    <TableCell align="right" onClick={() => { setSort((s) => ({ k, dir: s.k === k ? (s.dir === 1 ? -1 : 1) : -1 })); setVisivel(PAGE) }} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {label} <Box component="span" sx={{ opacity: 0.5, fontSize: 10 }}>{sort.k === k ? (sort.dir < 0 ? '▼' : '▲') : '⇅'}</Box>
    </TableCell>
  )
  const toggle = (k: string) => { setPst((p) => (p === k ? '' : k)); setVisivel(PAGE) }

  return (
    <Stack spacing={2}>
      <Typography variant="caption" color="text.secondary">
        O Mercado Livre compara o preço de cada anúncio com o do mesmo produto em outros vendedores e devolve um status e um preço de referência. Esta aba mostra só os <b>{nf(comparados.length)}</b> anúncios em que o ML fez a comparação; os outros <b>{nf(semComparacao)}</b> não têm concorrente identificado pelo ML. Clique num status para filtrar a lista.
      </Typography>

      <Grid container spacing={1.5}>
        {[
          { label: 'Acima do mercado', value: nf(caros.length), hint: `de ${nf(comparados.length)} comparados · clique: filtrar`, onClick: () => toggle('with_benchmark_highest'), selected: pst === 'with_benchmark_highest' },
          { label: `Receita em jogo (${diasJanela}d)`, value: fmtBrl(receitaCaros), hint: 'vendas dos anúncios acima do mercado' },
          { label: 'Diferença média', value: pctFmt(difMedia), hint: 'quanto acima da referência, nos acima do mercado' },
          { label: 'Excedente cobrado', value: fmtBrl(gapTotal), hint: `(preço − referência) × unidades vendidas em ${diasJanela}d` },
        ].map((k) => (
          <Grid key={k.label} size={{ xs: 6, md: 3 }}>
            <KpiCard label={k.label} value={k.value} hint={k.hint} onClick={k.onClick} selected={k.selected} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={1.5}>
        {porStatus.map((s) => {
          const on = pst === s.k
          const cor = t.palette[s.cor].main
          return (
            <Grid key={s.k} size={{ xs: 12, sm: 6, md: 3 }}>
              <Card onClick={() => toggle(s.k)} sx={{ height: '100%', cursor: 'pointer', outline: on ? `2px solid ${t.palette.primary.main}` : `1px solid ${alpha(cor, 0.5)}`, bgcolor: alpha(cor, 0.05) }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: cor }}>{s.label}</Typography>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: 24 }}>{nf(s.rows.length)}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{s.explica}</Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.75 }}>{fmtBrl(sum(s.rows, (r) => r.r))} em {diasJanela} dias</Typography>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontSize: 16, mr: 'auto' }}>Anúncios comparados pelo ML</Typography>
            <Typography variant="caption" color="text.secondary">{nf(lista.length)} de {nf(comparados.length)}</Typography>
            {pst || conta || busca ? <Button size="small" variant="outlined" onClick={() => { setPst(''); setConta(''); setBusca('') }}>Limpar</Button> : null}
          </Stack>
          <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}><TextField size="small" fullWidth label="Buscar" placeholder="Título ou ID" value={busca} onChange={(e) => { setBusca(e.target.value); setVisivel(PAGE) }} /></Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField select fullWidth size="small" label="Status no ML" value={pst} onChange={(e) => { setPst(e.target.value); setVisivel(PAGE) }}>
                <MenuItem value="">Todos os status</MenuItem>
                {porStatus.map((s) => <MenuItem key={s.k} value={s.k}>{s.label} ({nf(s.rows.length)})</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField select fullWidth size="small" label="Conta" value={conta} onChange={(e) => { setConta(e.target.value); setVisivel(PAGE) }}>
                <MenuItem value="">Todas as contas</MenuItem>
                {(['FESTA', 'HOME'] as const).map((k) => <MenuItem key={k} value={k}>{NOME_CONTA[k]} ({nf(comparados.filter((r) => r.acc === k).length)})</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
          {lista.length === 0 ? <ChartEmpty title="Nenhum anúncio com esses filtros." suggestPeriod={false} dense /> : (
            <>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 900, '& th': { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary', bgcolor: 'action.hover' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Anúncio</TableCell>
                      <TableCell>Status no ML</TableCell>
                      {th('pcur', 'Nosso preço')}
                      <TableCell align="right">Referência do ML</TableCell>
                      {th('pdif', 'Diferença')}
                      {th('pgap', 'Gap R$')}
                      {th('vi', `Visitas ${diasJanela}d`)}
                      <TableCell align="right">Un. {diasJanela}d</TableCell>
                      {th('r', `Receita ${diasJanela}d`)}
                      <TableCell align="right">Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lista.slice(0, visivel).map((r) => {
                      const s = PST[r.pst!] ?? { cor: 'warning' as const, label: r.pst! }
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ maxWidth: 320 }}>
                            {r.lk ? <Link href={r.lk} target="_blank" rel="noreferrer" underline="hover" noWrap sx={{ display: 'block', fontWeight: 600 }}>{r.t}</Link> : <Typography variant="body2" fontWeight={600} noWrap>{r.t}</Typography>}
                            <Typography variant="caption" color="text.secondary">{r.id} · {r.acc === 'FESTA' ? 'Festa' : 'Home'}{r.cat ? ' · catálogo' : ''}{r.bbox === false ? ' · buy box perdido' : ''}</Typography>
                          </TableCell>
                          <TableCell><Chip size="small" label={s.label} color={s.cor} variant="outlined" onClick={() => toggle(r.pst!)} /></TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{r.pcur != null ? fmtBrl(r.pcur) : r.p != null ? fmtBrl(r.p) : '—'}</TableCell>
                          <TableCell align="right">{r.psug != null ? fmtBrl(r.psug) : '—'}</TableCell>
                          {/* a API dá a diferença em módulo; o sinal vem do gap em R$ (nosso preço − referência) */}
                          <TableCell align="right" sx={{ color: r.pgap != null && r.pgap > 0 ? 'error.main' : r.pgap != null && r.pgap < 0 ? 'success.main' : undefined }}>
                            {r.pdif != null ? `${r.pgap != null && r.pgap > 0 ? '+' : r.pgap != null && r.pgap < 0 ? '−' : ''}${pctFmt(Math.abs(r.pdif))}` : '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ color: r.pgap != null && r.pgap > 0 ? 'error.main' : r.pgap != null && r.pgap < 0 ? 'success.main' : undefined }}>{r.pgap != null ? fmtBrl(r.pgap) : '—'}</TableCell>
                          <TableCell align="right">{nf(r.vi)}</TableCell>
                          <TableCell align="right">{nf(r.u)}</TableCell>
                          <TableCell align="right">{r.r ? fmtBrl(r.r) : '—'}</TableCell>
                          <TableCell align="right">{r.sc ?? '—'}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </Box>
              {visivel < lista.length ? <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}><Button variant="outlined" onClick={() => setVisivel((v) => v + PAGE)}>Carregar mais ({nf(lista.length - visivel)} restantes)</Button></Box> : null}
            </>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            "Referência do ML" é o preço que o Mercado Livre indica para ficar competitivo (price_to_win). "Gap R$" = nosso preço − referência. A margem de cada anúncio não está nesta aba — antes de baixar preço, conferir no painel de Promoções ou de Margem por Anúncio.
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  )
}
